import React, { useState, useEffect, useRef } from 'react';
import StatsOverview from './StatsOverview';
import WebsiteStatsOverview from './WebsiteStatsOverview';
import OrdersPage from '../orders/OrdersPage';
import { API_BASE_URL } from '../../config';
import { BarChart3, CalendarRange, Download, ListOrdered, RefreshCw } from 'lucide-react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

const PERIOD_CURRENT_MONTH = 'current_month';

const PERIOD_OPTIONS = [
    { id: 7, label: '7 дн.' },
    { id: 30, label: '30 дн.' },
    { id: 90, label: '90 дн.' },
    { id: PERIOD_CURRENT_MONTH, label: 'Текущий месяц' },
];

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getWebsiteRangeForPeriod = (period) => {
    const today = new Date();
    const from = new Date(today);
    if (period === PERIOD_CURRENT_MONTH) {
        from.setDate(1);
    } else {
        from.setDate(today.getDate() - Number(period || 30) + 1);
    }
    return {
        from: formatDateInput(from),
        to: formatDateInput(today),
    };
};

const DEFAULT_WEBSITE_RANGE = getWebsiteRangeForPeriod(30);

const buildDashboardUrl = (period, websiteRange) => {
    const params = new URLSearchParams();
    if (period === PERIOD_CURRENT_MONTH) {
        params.set('period', PERIOD_CURRENT_MONTH);
    } else {
        params.set('period_days', String(period));
    }
    if (websiteRange?.from && websiteRange?.to) {
        params.set('website_date_from', websiteRange.from);
        params.set('website_date_to', websiteRange.to);
    }
    return `${API_BASE_URL}/api/dashboard/?${params.toString()}`;
};

const TrendChart = ({
    title,
    data = [],
    suffix = '',
    lines = [],
    chartPeriodDays = 30,
    isCurrentMonthPeriod = false,
    periodLabel = '',
    loading = false,
}) => {
    const normalized = data.map((x) => ({ date: x.date, value: Number(x.value || 0), ...x }));
    const isCurrentMonth = Boolean(isCurrentMonthPeriod);
    const footer = periodLabel
        ? `Период: ${periodLabel} · наведите для значений`
        : isCurrentMonth
          ? `Текущий месяц (1–${chartPeriodDays}) · наведите для значений`
          : `Последние ${chartPeriodDays} дней · наведите для значений`;
    return (
        <div className="bg-white rounded-xl border border-slate-100 p-4 relative">
            <h4 className="font-semibold text-slate-800 mb-3">{title}</h4>
            <div className="h-72">
                {loading ? (
                    <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                        Загрузка графика…
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={normalized}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11 }}
                                interval="preserveStartEnd"
                                minTickGap={chartPeriodDays > 30 ? 28 : 12}
                            />
                            <YAxis tick={{ fontSize: 11 }} width={56} />
                            <Tooltip formatter={(v) => `${Number(v || 0).toLocaleString('ru-RU')}${suffix}`} />
                            <Legend />
                            {lines.map((line) => (
                                <Line
                                    key={line.dataKey}
                                    type="monotone"
                                    dataKey={line.dataKey}
                                    name={line.name}
                                    stroke={line.stroke}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
            <p className="text-xs text-slate-500 mt-2">{footer}</p>
        </div>
    );
};

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartsLoading, setChartsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState(30);
    const [websiteView, setWebsiteView] = useState('statistics');
    const [websiteRangeDraft, setWebsiteRangeDraft] = useState(DEFAULT_WEBSITE_RANGE);
    const [websiteRange, setWebsiteRange] = useState(DEFAULT_WEBSITE_RANGE);
    const [websiteRangeError, setWebsiteRangeError] = useState('');
    const [websiteExporting, setWebsiteExporting] = useState(false);
    const hasLoadedOnce = useRef(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        const controller = new AbortController();

        const fetchDashboardData = async () => {
            const isInitial = !hasLoadedOnce.current;
            try {
                if (isInitial) {
                    setLoading(true);
                } else {
                    setChartsLoading(true);
                }
                const response = await fetch(buildDashboardUrl(period, websiteRange), {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                if (requestId !== requestIdRef.current) return;
                setStats(data);
                setError(null);
                hasLoadedOnce.current = true;
            } catch (err) {
                if (err?.name === 'AbortError') return;
                console.error('Error fetching dashboard data:', err);
                if (requestId !== requestIdRef.current) return;
                setError('Не удалось загрузить данные дашборда');
            } finally {
                if (requestId !== requestIdRef.current) return;
                setLoading(false);
                setChartsLoading(false);
            }
        };

        fetchDashboardData();
        return () => controller.abort();
    }, [period, websiteRange]);

    const setPresetPeriod = (nextPeriod) => {
        const nextWebsiteRange = getWebsiteRangeForPeriod(nextPeriod);
        setPeriod(nextPeriod);
        setWebsiteRangeDraft(nextWebsiteRange);
        setWebsiteRange(nextWebsiteRange);
        setWebsiteRangeError('');
    };

    const validateWebsiteRange = () => {
        if (!websiteRangeDraft.from || !websiteRangeDraft.to) {
            return 'Укажите обе даты периода';
        }
        if (websiteRangeDraft.from > websiteRangeDraft.to) {
            return 'Дата начала не может быть позже даты окончания';
        }
        return '';
    };

    const applyWebsiteRange = () => {
        const validationError = validateWebsiteRange();
        if (validationError) {
            setWebsiteRangeError(validationError);
            return;
        }
        setWebsiteRangeError('');
        setWebsiteRange({ ...websiteRangeDraft });
    };

    const downloadWebsiteReport = async () => {
        const validationError = validateWebsiteRange();
        if (validationError) {
            setWebsiteRangeError(validationError);
            return;
        }

        const exportRange = { ...websiteRangeDraft };
        if (exportRange.from !== websiteRange.from || exportRange.to !== websiteRange.to) {
            setWebsiteRange(exportRange);
        }

        try {
            setWebsiteExporting(true);
            setWebsiteRangeError('');
            const params = new URLSearchParams({
                date_from: exportRange.from,
                date_to: exportRange.to,
            });
            const response = await fetch(
                `${API_BASE_URL}/api/dashboard/website-report.csv?${params.toString()}`,
                { credentials: 'include' },
            );
            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                throw new Error(payload?.detail || `HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `website-purchases_${exportRange.from}_${exportRange.to}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting website report:', err);
            setWebsiteRangeError(`Не удалось скачать отчёт: ${err.message}`);
        } finally {
            setWebsiteExporting(false);
        }
    };

    const header = (
        <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-slate-800">Обзор показателей</h2>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-500">Период</span>
                {PERIOD_OPTIONS.map((opt) => (
                    <button
                        key={String(opt.id)}
                        type="button"
                        disabled={chartsLoading || loading}
                        onClick={() => {
                            setPresetPeriod(opt.id);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-60 ${
                            period === opt.id
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );

    if (loading && !stats) {
        return (
            <div className="space-y-6 animate-fade-in">
                {header}
                <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">Загрузка данных...</div>
                </div>
            </div>
        );
    }

    if (error && !stats) {
        return (
            <div className="space-y-6 animate-fade-in">
                {header}
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-600">
                    {error}
                </div>
            </div>
        );
    }

    if (!stats) {
        return null;
    }

    const chartPeriod = Number(stats.chart_period_days || (typeof period === 'number' ? period : 30));
    const isCurrentMonthPeriod =
        stats.period_mode === PERIOD_CURRENT_MONTH || period === PERIOD_CURRENT_MONTH;
    const periodLabel =
        stats.period_label ||
        (isCurrentMonthPeriod ? 'текущий месяц' : `${period} дн.`);
    const websitePeriodDays = Number(stats.website?.period_days || chartPeriod);
    const websitePeriodLabel = stats.website?.period_label || periodLabel;
    const websiteRangeChanged =
        websiteRangeDraft.from !== websiteRange.from || websiteRangeDraft.to !== websiteRange.to;

    return (
        <div className="space-y-6 animate-fade-in">
            {header}
            {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-600">
                    {error}
                </div>
            )}
            <StatsOverview stats={stats} periodLabel={periodLabel} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TrendChart
                    title="Регистрации по дням"
                    data={(stats.daily_registrations || []).map((d) => ({ ...d, registrations: Number(d.value || 0) }))}
                    lines={[{ dataKey: 'registrations', name: 'Регистрации', stroke: '#2563eb' }]}
                    chartPeriodDays={chartPeriod}
                    isCurrentMonthPeriod={isCurrentMonthPeriod}
                    loading={chartsLoading}
                />
                <TrendChart
                    title="Обороты по дням"
                    data={(stats.daily_revenue || []).map((d, i) => ({
                        date: d.date,
                        total_revenue: Number(d.value || 0),
                        referral_revenue: Number((stats.daily_referral_revenue || [])[i]?.value || 0),
                    }))}
                    suffix=" ₽"
                    lines={[
                        { dataKey: 'total_revenue', name: 'Общий оборот', stroke: '#059669' },
                        { dataKey: 'referral_revenue', name: 'Реферальный оборот', stroke: '#7c3aed' },
                    ]}
                    chartPeriodDays={chartPeriod}
                    isCurrentMonthPeriod={isCurrentMonthPeriod}
                    loading={chartsLoading}
                />
                <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col justify-center min-w-0">
                    <h4 className="font-semibold text-slate-800 mb-2">Остаток по реферальным ботам</h4>
                    <p className="text-2xl sm:text-3xl font-bold text-indigo-700 tabular-nums break-all">
                        {Number(stats.referral_bots_balance_total || 0).toLocaleString('ru-RU', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}{' '}
                        ₽
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-2xl font-bold text-slate-800">Сайт</h2>
                <div
                    className="inline-flex w-full rounded-lg border border-slate-200 bg-slate-100 p-1 sm:w-auto"
                    role="tablist"
                    aria-label="Данные сайта"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={websiteView === 'statistics'}
                        onClick={() => setWebsiteView('statistics')}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors sm:flex-none ${
                            websiteView === 'statistics'
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        <BarChart3 size={17} aria-hidden="true" />
                        Статистика
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={websiteView === 'orders'}
                        onClick={() => setWebsiteView('orders')}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors sm:flex-none ${
                            websiteView === 'orders'
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        <ListOrdered size={17} aria-hidden="true" />
                        Заказы сайта
                    </button>
                </div>
            </div>

            {websiteView === 'statistics' ? (
                <>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                    <CalendarRange size={20} aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">Период отчёта</h3>
                                    <p className="mt-1 text-sm text-slate-500">
                                        Статистика и CSV включают успешные покупки с сайта и UTM-метки.
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] lg:items-end">
                                <label className="flex min-w-0 flex-col gap-1 text-sm text-slate-600">
                                    С
                                    <input
                                        type="date"
                                        value={websiteRangeDraft.from}
                                        max={websiteRangeDraft.to || undefined}
                                        onChange={(event) => {
                                            setWebsiteRangeDraft((current) => ({
                                                ...current,
                                                from: event.target.value,
                                            }));
                                            setWebsiteRangeError('');
                                        }}
                                        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                    />
                                </label>
                                <label className="flex min-w-0 flex-col gap-1 text-sm text-slate-600">
                                    По
                                    <input
                                        type="date"
                                        value={websiteRangeDraft.to}
                                        min={websiteRangeDraft.from || undefined}
                                        max={formatDateInput(new Date())}
                                        onChange={(event) => {
                                            setWebsiteRangeDraft((current) => ({
                                                ...current,
                                                to: event.target.value,
                                            }));
                                            setWebsiteRangeError('');
                                        }}
                                        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                    />
                                </label>
                                <button
                                    type="button"
                                    onClick={applyWebsiteRange}
                                    disabled={!websiteRangeChanged || chartsLoading}
                                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <RefreshCw size={17} aria-hidden="true" />
                                    Применить
                                </button>
                                <button
                                    type="button"
                                    onClick={downloadWebsiteReport}
                                    disabled={websiteExporting}
                                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <Download size={17} aria-hidden="true" />
                                    {websiteExporting ? 'Формирование…' : 'Скачать CSV'}
                                </button>
                            </div>
                        </div>
                        {websiteRangeError && (
                            <p className="mt-3 text-sm text-rose-600" role="alert">
                                {websiteRangeError}
                            </p>
                        )}
                    </div>

                    <WebsiteStatsOverview stats={stats.website || {}} periodLabel={websitePeriodLabel} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TrendChart
                            title="Регистрации по дням"
                            data={(stats.website?.daily_registrations || []).map((d) => ({
                                ...d,
                                registrations: Number(d.value || 0),
                            }))}
                            lines={[{ dataKey: 'registrations', name: 'Регистрации', stroke: '#2563eb' }]}
                            chartPeriodDays={websitePeriodDays}
                            periodLabel={websitePeriodLabel}
                            loading={chartsLoading}
                        />
                        <TrendChart
                            title="Обороты по дням"
                            data={(stats.website?.daily_revenue || []).map((d, i) => ({
                                date: d.date,
                                total_revenue: Number(d.value || 0),
                                stars_revenue: Number((stats.website?.daily_stars_revenue || [])[i]?.value || 0),
                            }))}
                            suffix=" ₽"
                            lines={[
                                { dataKey: 'total_revenue', name: 'Общий оборот', stroke: '#059669' },
                                { dataKey: 'stars_revenue', name: 'Выручка Stars', stroke: '#7c3aed' },
                            ]}
                            chartPeriodDays={websitePeriodDays}
                            periodLabel={websitePeriodLabel}
                            loading={chartsLoading}
                        />
                        <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col justify-center min-w-0">
                            <h4 className="font-semibold text-slate-800 mb-2">Средний чек сайта</h4>
                            <p className="text-2xl sm:text-3xl font-bold text-indigo-700 tabular-nums break-all">
                                {Number(stats.website?.average_check || 0).toLocaleString('ru-RU', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}{' '}
                                ₽
                            </p>
                        </div>
                    </div>
                </>
            ) : (
                <OrdersPage orderSource="website" title="Заказы сайта" embedded />
            )}
        </div>
    );
};

export default Dashboard;
